import { Policy } from '../../types';
import { policiesForCriterion } from './criteriaPolicySearch';

/**
 * A Criterion's Policy Versions, oldest first.
 *
 * `policiesForCriterion` is newest-first for the list UI. History navigation steps
 * toward older versions, so the index math is simpler on the reversed order.
 * Version number is the authority (ADR-0001).
 */
export const lineageOldestFirst = (policies: Policy[], criteriaId: string): Policy[] =>
  [...policiesForCriterion(policies, criteriaId)].reverse();

export type HistorySelection = {
  selectedId: string;
  comparisonId: string | null;
  comparisonPinned: boolean;
};

export const previousInLineage = (lineage: Policy[], selectedId: string): Policy | undefined => {
  const index = lineage.findIndex(policy => policy.id === selectedId);
  if (index <= 0) return undefined;
  return lineage[index - 1];
};

export const canStep = (lineage: Policy[], selectedId: string, direction: -1 | 1): boolean => {
  const index = lineage.findIndex(policy => policy.id === selectedId);
  if (index < 0) return false;
  const next = index + direction;
  return next >= 0 && next < lineage.length;
};

export const defaultHistorySelection = (
  lineage: Policy[],
  currentPolicyId: string | null
): HistorySelection | null => {
  if (!currentPolicyId || !lineage.some(policy => policy.id === currentPolicyId)) {
    return null;
  }
  const previous = previousInLineage(lineage, currentPolicyId);
  return {
    selectedId: currentPolicyId,
    comparisonId: previous?.id ?? null,
    comparisonPinned: false,
  };
};

/**
 * Point selected at `selectedId`. In auto mode, comparison stays glued to the
 * immediately older version; once the user has pinned comparison, it stays put.
 */
export const applySelected = (
  lineage: Policy[],
  selection: HistorySelection,
  selectedId: string
): HistorySelection => {
  if (selection.comparisonPinned) {
    return { ...selection, selectedId };
  }
  const previous = previousInLineage(lineage, selectedId);
  return {
    selectedId,
    comparisonId: previous?.id ?? null,
    comparisonPinned: false,
  };
};

export const moveSelected = (
  lineage: Policy[],
  selection: HistorySelection,
  direction: -1 | 1
): HistorySelection => {
  const index = lineage.findIndex(policy => policy.id === selection.selectedId);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= lineage.length) return selection;
  const next = lineage[nextIndex];
  if (!next) return selection;
  return applySelected(lineage, selection, next.id);
};

/**
 * Choosing anything other than the auto previous pins comparison until the dialog
 * closes. Picking the auto previous while still unpinned leaves auto mode on.
 */
export const applyComparison = (
  lineage: Policy[],
  selection: HistorySelection,
  comparisonId: string
): HistorySelection => {
  const autoId = previousInLineage(lineage, selection.selectedId)?.id ?? null;
  return {
    selectedId: selection.selectedId,
    comparisonId,
    comparisonPinned: selection.comparisonPinned || comparisonId !== autoId,
  };
};

/** Chronological order for DiffPane: older version is the minus side. */
export const orderedDiffPair = (
  selected: Policy,
  comparison: Policy
): { older: Policy; newer: Policy } => {
  const byVersion = (left: Policy, right: Policy) => {
    const leftVersion = left.version ?? Number.NEGATIVE_INFINITY;
    const rightVersion = right.version ?? Number.NEGATIVE_INFINITY;
    if (leftVersion !== rightVersion) return leftVersion - rightVersion;
    return new Date(left.creationDate).getTime() - new Date(right.creationDate).getTime();
  };
  return selected.id === comparison.id || byVersion(selected, comparison) <= 0
    ? { older: selected, newer: comparison }
    : { older: comparison, newer: selected };
};
